/**
 * `<GettingStartedChecklist>` — paying-tenant first-run getting-started panel.
 *
 * Source-of-truth brief: ``backend/docs/cofounder_brief_paying_tenant_onboarding.md``.
 *
 * Renders ONLY for a provisioned PAID, non-demo, first-run tenant — the
 * `first_run` gate is server-computed in GET /getting-started (true only when
 * the tenant has a tenant_contract, is not a demo tenant, and the checklist is
 * neither dismissed nor complete). Demo tenants (their own seeded experience)
 * and non-provisioned tenants never see it.
 *
 * Lists the core first-run path with completion checkmarks + a primary CTA per
 * step. Steps tick off as the tenant completes them (server-driven via
 * `completed.*`); the whole panel disappears once every step is done OR the
 * tenant dismisses it. Mounted on the Dashboard (the app landing surface).
 */
import { Link } from "react-router-dom";
import { useGettingStarted } from "@/hooks/queries/auth/useGettingStarted";
import { useDismissGettingStarted } from "@/hooks/queries/auth/useDismissGettingStarted";

interface Step {
  /** Stable id for testing. */
  id: string;
  title: string;
  /** Completer items — step is done if ANY is set. */
  done: boolean;
  cta: { label: string; to?: string };
  /** Optional secondary action (e.g. Greenhouse import). */
  secondary?: { label: string; to: string };
}

const CheckCircle = (): JSX.Element => (
  <svg
    viewBox="0 0 20 20"
    className="h-5 w-5 shrink-0 text-status-hired-fg"
    fill="currentColor"
    aria-hidden
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.293a1 1 0 0 0-1.414-1.414L9 10.586 7.707 9.293a1 1 0 0 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z"
      clipRule="evenodd"
    />
  </svg>
);

const EmptyCircle = (): JSX.Element => (
  <svg
    viewBox="0 0 20 20"
    className="h-5 w-5 shrink-0 text-text-tertiary"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden
  >
    <circle cx="10" cy="10" r="7.25" strokeDasharray="2 2.5" />
  </svg>
);

export function GettingStartedChecklist({
  basePath,
}: {
  /** Prefix-aware link base for the tree this is rendered in — `/${slug}` or
   *  `/${slug}/v1` for v1, `/${slug}/v2` for v2. Built by the parent (v1:
   *  `useTenantV1Base()`, v2: `useTenantBase()`); this component is shared by
   *  both trees and must never assume a tree of its own. */
  basePath: string | undefined;
}): JSX.Element | null {
  const { data } = useGettingStarted();
  const dismiss = useDismissGettingStarted();

  // Server is the single source of truth for the gate.
  if (!data || !data.first_run) return null;

  const base = basePath ?? "";
  const c = data.completed;

  const steps: Step[] = [
    {
      id: "create-jd",
      title: "Create your first job",
      done: c.create_first_jd != null,
      cta: { label: "Create job", to: `${base}/jds/new` },
    },
    {
      id: "add-candidates",
      // Satisfied by a manual upload OR a Greenhouse connect/import.
      title: "Add candidates",
      done: c.upload_first_resume != null || c.connect_greenhouse != null,
      cta: { label: "Add candidate", to: `${base}/candidates/new` },
      secondary: {
        label: "Import from Greenhouse",
        to: `${base}/settings/integrations`,
      },
    },
    {
      id: "run-screen",
      title: "Run your first screen",
      done: c.run_first_evaluation != null,
      cta: { label: "View ranking", to: `${base}/dashboard` },
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;

  return (
    <section
      data-testid="getting-started-checklist"
      aria-label="Getting started"
      className="rounded-xl border border-border-default bg-surface-card p-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-text-emphasis">
            Get your first hire ranked
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            {doneCount} of {steps.length} done — three steps to your first
            shortlist.
          </p>
        </div>
        <button
          type="button"
          data-testid="getting-started-dismiss"
          onClick={() => dismiss.mutate()}
          disabled={dismiss.isPending}
          className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-card-hover hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-border-focus disabled:opacity-60"
        >
          Dismiss
        </button>
      </div>

      <ol className="mt-4 space-y-3">
        {steps.map((step) => (
          <li
            key={step.id}
            data-testid={`gs-step-${step.id}`}
            data-done={step.done}
            className="flex items-center gap-3"
          >
            {step.done ? <CheckCircle /> : <EmptyCircle />}
            <span
              className={
                step.done
                  ? "flex-1 text-sm text-text-tertiary line-through"
                  : "flex-1 text-sm font-medium text-text-primary"
              }
            >
              {step.title}
            </span>
            {!step.done && (
              <div className="flex shrink-0 items-center gap-2">
                {step.secondary && (
                  <Link
                    to={step.secondary.to}
                    data-testid={`gs-step-${step.id}-secondary`}
                    className="rounded-md px-2.5 py-1 text-xs font-medium text-accent-secondary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                  >
                    {step.secondary.label}
                  </Link>
                )}
                {step.cta.to && (
                  <Link
                    to={step.cta.to}
                    data-testid={`gs-step-${step.id}-cta`}
                    className="rounded-md border border-border-default bg-surface-elevated px-3 py-1 text-xs font-semibold text-text-primary hover:border-border-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                  >
                    {step.cta.label}
                  </Link>
                )}
              </div>
            )}
          </li>
        ))}
      </ol>

      {/* (d) Reassurance: account-level guardrails are already on. Informational
          only — no admin-gated data is read into this recruiter-visible panel. */}
      <p className="mt-4 border-t border-border-default pt-3 text-xs text-text-tertiary">
        MFA and screening caps for your account are managed in{" "}
        <Link
          to={`${base}/settings/security`}
          className="font-medium text-accent-secondary hover:underline"
        >
          Settings &rarr; Security
        </Link>
        .
      </p>
    </section>
  );
}
